import React, { createRef } from "react";
import "react-bootstrap-typeahead/css/Typeahead.css";
import { Link, withRouter } from "react-router-dom";
import { Breadcrumb, Button, Container } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faCheck,
    faCog,
    faDownload,
    faEnvelope,
    faPlus,
    faTimes,
} from "@fortawesome/free-solid-svg-icons";

import Brand from "services/BrandService";
import Toast from "services/Toast";
import MyTable from "components/MyTable";
import prompt from "components/Prompt";
import ProcessingButton from "components/ProcessingButton";
import AuthService from "services/AuthService";
import GenericService from "services/GenericService";

/**
 * @component
 * @category Scenes
 * @subcategory Brand
 */
class List extends React.Component {
    constructor(props) {
        super(props);
        this.tableRef = createRef();
        this.state = {
            downloading: false,
            countries: ["United States", "Canada"],
            categories: [],
            modules: [],
            country_settings: ["USA (N)", "USA (Y)", "CAN (N)", "CAN (Y)"],
        };
        this.downloadSpreadsheet = this.downloadSpreadsheet.bind(this);
        this.createNewBrand = this.createNewBrand.bind(this);
        this.allowedToCreateNewBrands = AuthService.isAdmin();
    }

    componentDidMount() {
        Brand.all().then((data) => {
            if (data === false) {
                Toast.error(
                    "Something went wrong while loading the list of brands"
                );
                return;
            }
            let countries = {};
            let categories = {};
            const country_codes = new Set();
            const country_settings = [];

            data.forEach((row) => {
                let list =
                    typeof row["countries"] === "string"
                        ? row["countries"].split(", ")
                        : [];
                list.forEach((element) => {
                    if (element.length) {
                        countries[element] = null;
                    }
                });
                row["categories"].forEach((category) => {
                    categories[category.name] = null;
                });

                // country settings - using general smtp only as a stand-in for all settings columns as they all have the same list of countries
                const settingsList =
                    typeof row["general_smtp"] === "string"
                        ? row["general_smtp"].split(", ")
                        : [];
                settingsList.forEach((element) => {
                    if (element.length) {
                        country_codes.add(element.split(" ")[0]);
                    }
                });
            });

            country_codes.forEach((code) => {
                country_settings.push(`${code} (N)`);
                country_settings.push(`${code} (Y)`);
            });

            this.setState(
                {
                    countries: Object.keys(countries),
                    categories: Object.keys(categories).sort(),
                    country_settings: country_settings.sort((a, b) => {
                        const aHasUS = a.includes("USA");
                        const bHasUS = b.includes("USA");
                        const aHasCanada = a.includes("CAN");
                        const bHasCanada = b.includes("CAN");

                        if (aHasUS && !bHasUS) {
                            return -1;
                        }
                        if (!aHasUS && bHasUS) {
                            return 1;
                        }
                        if (aHasCanada && !bHasCanada && !bHasUS) {
                            return -1;
                        }
                        if (!aHasCanada && bHasCanada && !aHasUS) {
                            return 1;
                        }
                        return a.localeCompare(b);
                    }),
                },
                () => this.tableRef.current?.populate(data)
            );
        });
        GenericService.modules().then((data) => {
            if (!Array.isArray(data)) {
                Toast.error(
                    "Something went wrong while loading the list of modules"
                );
                return;
            }
            const modules = data
                .filter((module) => module.active !== false)
                .map((module) => ({
                    label: `${module.acronym} - ${module.title}`,
                    value: module.acronym,
                }));
            this.setState({ modules });
        });
    }

    createNewBrand() {
        prompt("Create new Brand", "Create", "Cancel", "Brand name:", "")
            .then((evt) => {
                const name = evt.value.trim();
                if (name.length) {
                    Brand.new(name).then((id) => {
                        if (id === false) {
                            Toast.error(
                                "Something went wrong while creating the new organization"
                            );
                            return;
                        }
                        this.props.history.push("/brand/" + id + "#profile");
                        Toast.ok(
                            "Brand created successfully. DO NOT forget to associate it to an organization!",
                            5000
                        );
                    });
                }
            })
            .catch(() => {});
    }

    downloadSpreadsheet() {
        this.tableRef.current.CSV();
    }

    render() {
        return (
            <Container className="pt-4">
                <span
                    style={{
                        float: "right",
                    }}
                >
                    <Button
                        size="sm"
                        className="tdButton"
                        onClick={this.createNewBrand}
                        disabled={!this.allowedToCreateNewBrands}
                    >
                        <FontAwesomeIcon
                            icon={faPlus}
                            style={{
                                marginRight: "5px",
                            }}
                        />
                        New Brand
                    </Button>
                    <ProcessingButton
                        size="sm"
                        processing={this.state.downloading}
                        processingLabel="Downloading ..."
                        variant="success"
                        label="Download CSV"
                        icon={faDownload}
                        onClick={this.downloadSpreadsheet}
                    />
                </span>
                <Breadcrumb className="dd">
                    <Breadcrumb.Item>Dashboard</Breadcrumb.Item>
                    <Breadcrumb.Item active>List</Breadcrumb.Item>
                </Breadcrumb>
                <MyTable
                    name="brand-list"
                    dynamic={false}
                    ref={this.tableRef}
                    sortBy="created_at"
                    sortOrder="desc"
                    headers={[
                        {
                            field: "id",
                            label: "ID",
                            sortable: true,
                            searchable: true,
                            width: "50px",
                        },
                        {
                            field: "name",
                            label: "Dashboard",
                            sortable: true,
                            searchable: true,
                            width: "150px",
                        },
                        {
                            field: "organization",
                            label: "Organization",
                            sortable: true,
                            searchable: true,
                            width: "150px",
                        },
                        {
                            field: "status",
                            label: "Status",
                            width: "70px",
                            sortable: true,
                            allowEmpty: true,
                            options: [
                                { label: "Active", value: "1" },
                                { label: "Disabled", value: "0" },
                            ],
                        },
                        {
                            field: "manufacturers",
                            label: "Manufacturers",
                            sortable: true,
                            searchable: true,
                            width: "150px",
                        },
                        {
                            field: "categories",
                            label: "Categories",
                            allowEmpty: true,
                            options: this.state.categories,
                            width: "150px",
                        },
                        {
                            field: "modules",
                            label: "Modules",
                            width: "150px",
                            sortable: true,
                            allowEmpty: true,
                            options: this.state.modules,
                        },
                        {
                            field: "countries",
                            label: "Countries",
                            sortable: true,
                            allowEmpty: true,
                            options: this.state.countries,
                            width: "150px",
                        },
                        {
                            field: "sales_status",
                            label: "Sales Status",
                            sortable: true,
                            allowEmpty: true,
                            options: [
                                { label: "Unknown", value: "Unknown" },
                                {
                                    label: "Demo - Build In Progress",
                                    value: "Demo - Build In Progress",
                                },
                                {
                                    label: "Demo - Ready",
                                    value: "Demo - Ready",
                                },
                                {
                                    label: "Demo - Brand Still Evaluating",
                                    value: "Demo - Brand Still Evaluating",
                                },
                                {
                                    label: "Demo - Status Unknown",
                                    value: "Demo - Status Unknown",
                                },
                                {
                                    label: "Demo - Did Not Convert",
                                    value: "Demo - Did Not Convert",
                                },
                                {
                                    label: "Trial - Free",
                                    value: "Trial - Free",
                                },
                                {
                                    label: "Trial - Free (Special Offer)",
                                    value: "Trial - Free (Special Offer)",
                                },
                                {
                                    label: "Trial - Paid",
                                    value: "Trial - Paid",
                                },
                                {
                                    label: "Trial - Did Not Convert",
                                    value: "Trial - Did Not Convert",
                                },
                                {
                                    label: "Paying/Active",
                                    value: "Paying/Active",
                                },
                                { label: "Delinquent", value: "Delinquent" },
                                {
                                    label: "Canceled - Churned/Lost",
                                    value: "Canceled - Churned/Lost",
                                },
                                {
                                    label: "Products Moved",
                                    value: "Products Moved",
                                },
                                {
                                    label: "Mistake / Duplicate",
                                    value: "Mistake / Duplicate",
                                },
                                {
                                    label: "Paying/Active - Untracked Account",
                                    value: "Paying/Active - Untracked Account",
                                },
                                {
                                    label: "Used for Testing",
                                    value: "Used for Testing",
                                },
                                { label: "Demo - PDLT", value: "Demo - PDLT" },
                                {
                                    label: "Disabled - PDLT",
                                    value: "Disabled - PDLT",
                                },
                            ],
                            width: "150px",
                        },
                        {
                            field: "constraints",
                            label: "W/B",
                            hint: "Whitelist/Blacklist",
                            width: "50px",
                            sortable: true,
                            allowEmpty: true,
                            options: [
                                { label: "Whitelist", value: "W" },
                                { label: "Blacklist", value: "B" },
                            ],
                        },
                        {
                            field: "products_owner",
                            label: "# Owner Products",
                            width: "140px",
                            sortable: true,
                            searchable: true,
                            hint: "The # of products with Owner status",
                        },
                        {
                            field: "products_viewer",
                            label: "# Viewer Products",
                            width: "140px",
                            sortable: true,
                            searchable: true,
                            hint: "The # of products with Viewer status",
                        },
                        {
                            field: "enforcement_smtp",
                            label: "Enforcement SMTP",
                            width: "130px",
                            allowEmpty: true,
                            allowMultiple: true,
                            options: this.state.country_settings,
                        },
                        {
                            field: "general_smtp",
                            label: "General SMTP",
                            width: "130px",
                            allowEmpty: true,
                            allowMultiple: true,
                            options: this.state.country_settings,
                        },
                        {
                            field: "workflow_notices",
                            label: "Workflow Notices",
                            width: "130px",
                            allowEmpty: true,
                            allowMultiple: true,
                            options: this.state.country_settings,
                        },
                        {
                            field: "created_at",
                            label: "Created",
                            width: "80px",
                            sortable: true,
                            searchable: true,
                        },
                    ]}
                    renderRows={{
                        className: (row) =>
                            row.status !== "1" ? "blocked" : "",
                    }}
                    renderColumns={{
                        id: (row) => (
                            <Link className="link" to={`/brand/${row.id}`}>
                                {row.id}
                            </Link>
                        ),
                        name: (row) => (
                            <Link className="link" to={`/brand/${row.id}`}>
                                {row.name}
                            </Link>
                        ),
                        organization: (row) => (
                            <Link
                                className="link"
                                to={`/organization/${row.org_id}`}
                            >
                                {row.organization}
                            </Link>
                        ),
                        manufacturers: {
                            text: (row) =>
                                row.manufacturers.length
                                    ? row.manufacturers
                                          .map((r) => {
                                              return r.name;
                                          })
                                          .join("; ")
                                    : "",
                            csv: (row) =>
                                row.manufacturers
                                    .map((r) => {
                                        return "#" + r.id + " " + r.name;
                                    })
                                    .join("; "),
                            format: (row) =>
                                row.manufacturers
                                    .map((r) => {
                                        return r.name;
                                    })
                                    .join("; "),
                        },
                        categories: {
                            text: (row) =>
                                row.categories.length
                                    ? row.categories
                                          .map((r) => {
                                              return r.name;
                                          })
                                          .sort()
                                          .join("; ")
                                    : "",
                            csv: (row) =>
                                row.categories
                                    .map((r) => {
                                        return "#" + r.id + " " + r.name;
                                    })
                                    .sort()
                                    .join("; "),
                            format: (row) =>
                                row.categories
                                    .map((r) => {
                                        return r.name;
                                    })
                                    .sort()
                                    .join("; "),
                        },
                        status: {
                            csv: (row) =>
                                row.status === "1" ? "Active" : "Disabled",
                            format: (row) =>
                                row.status === "1" ? (
                                    <FontAwesomeIcon
                                        style={{
                                            color: "green",
                                        }}
                                        icon={faCheck}
                                    />
                                ) : (
                                    <FontAwesomeIcon
                                        style={{ color: "red" }}
                                        icon={faTimes}
                                    />
                                ),
                            className: "text-center",
                        },
                        new_workflow_engine: {
                            csv: (row) => {
                                let content = [];
                                if (
                                    row.new_workflow_engine.indexOf("E") !== -1
                                ) {
                                    content.push("Engine");
                                }
                                if (
                                    row.new_workflow_engine.indexOf("N") !== -1
                                ) {
                                    content.push("Notification");
                                }
                                return content.join(", ");
                            },
                            format: (row) => {
                                return (
                                    <React.Fragment>
                                        <FontAwesomeIcon
                                            icon={faCog}
                                            style={
                                                row.new_workflow_engine.indexOf(
                                                    "E"
                                                ) !== -1
                                                    ? { color: "green" }
                                                    : { color: "silver" }
                                            }
                                        />
                                        &nbsp;
                                        <FontAwesomeIcon
                                            icon={faEnvelope}
                                            style={
                                                row.new_workflow_engine.indexOf(
                                                    "N"
                                                ) !== -1
                                                    ? { color: "green" }
                                                    : { color: "silver" }
                                            }
                                        />
                                    </React.Fragment>
                                );
                            },
                            className: "text-center",
                        },
                        constraints: {
                            csv: (row) =>
                                row.constraints.indexOf("B") !== -1
                                    ? "Blacklist"
                                    : row.constraints.indexOf("W") !== -1
                                    ? "Whitelist"
                                    : "",
                            className: "text-center bold",
                        },
                        products_owner: {
                            className: "text-center",
                        },
                        products_viewer: {
                            className: "text-center",
                        },
                    }}
                />
            </Container>
        );
    }
}

export default withRouter(List);
